import type { BmiUnits } from '@/features/storefront/sections/schema';

/**
 * Body mass index, worked out in the shopper's browser.
 *
 * Nothing here is stored or sent: the numbers a shopper types live in the
 * form's state and go when the page does. The ranges are generous on purpose -
 * they refuse a typo (a height of 17, a weight of 7000), not an unusual body.
 */

/** The adult categories the WHO draws the line between. */
export type BmiCategory = 'under' | 'healthy' | 'over' | 'obese';

export const BMI_HEIGHT_CM = { min: 50, max: 250 } as const;
export const BMI_WEIGHT_KG = { min: 20, max: 300 } as const;

const CM_PER_INCH = 2.54;
const KG_PER_POUND = 0.45359237;

/** The unit a shopper measures one thing in, switchable on the form. */
export type BmiHeightUnit = 'cm' | 'ft';
export type BmiWeightUnit = 'kg' | 'lb';

/** The units a section opens in: the merchant's choice of system. */
export function defaultBmiUnits(units: BmiUnits): { height: BmiHeightUnit; weight: BmiWeightUnit } {
  return units === 'imperial' ? { height: 'ft', weight: 'lb' } : { height: 'cm', weight: 'kg' };
}

/** What a shopper typed, as the form holds it: strings, possibly empty. */
export interface BmiInput {
  units: BmiUnits;
  /** Overrides `units` for the height alone - the shopper switched it. */
  heightUnit?: BmiHeightUnit;
  /** Overrides `units` for the weight alone. */
  weightUnit?: BmiWeightUnit;
  /** Centimetres, or feet. */
  height: string;
  /** Inches past the feet; imperial only. */
  inches?: string;
  /** Kilograms (metric) or pounds (imperial). */
  weight: string;
}

export interface BmiResult {
  /** Rounded to one decimal place. */
  value: number;
  category: BmiCategory;
}

/** A typed number, reading a comma as a decimal point; `null` when it is not one. */
function readNumber(raw: string | undefined): number | null {
  const text = (raw ?? '').trim().replace(',', '.');
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** The category a (rounded) BMI falls in. */
export function bmiCategory(value: number): BmiCategory {
  if (value < 18.5) return 'under';
  if (value < 25) return 'healthy';
  if (value < 30) return 'over';
  return 'obese';
}

/**
 * The BMI for what was typed, or `null` when it is missing or out of range.
 *
 * Imperial is converted to metric first and held to the same range, so the
 * two units refuse the same bodies.
 */
export function calculateBmi(input: BmiInput): BmiResult | null {
  const units = defaultBmiUnits(input.units);
  let heightCm: number | null;
  if ((input.heightUnit ?? units.height) === 'ft') {
    const feet = readNumber(input.height);
    const inches = readNumber(input.inches) ?? 0;
    if (feet === null || feet < 0 || inches < 0 || inches >= 12) return null;
    heightCm = (feet * 12 + inches) * CM_PER_INCH;
  } else {
    heightCm = readNumber(input.height);
  }
  let weightKg = readNumber(input.weight);
  if (weightKg !== null && (input.weightUnit ?? units.weight) === 'lb') weightKg *= KG_PER_POUND;
  if (heightCm === null || weightKg === null) return null;
  if (heightCm < BMI_HEIGHT_CM.min || heightCm > BMI_HEIGHT_CM.max) return null;
  if (weightKg < BMI_WEIGHT_KG.min || weightKg > BMI_WEIGHT_KG.max) return null;
  const metres = heightCm / 100;
  const value = Math.round((weightKg / (metres * metres)) * 10) / 10;
  return { value, category: bmiCategory(value) };
}

/** The span the scale bar draws, and where each category starts on it. */
export const BMI_SCALE = { min: 15, max: 40 } as const;
export const BMI_BANDS: readonly { category: BmiCategory; from: number; to: number }[] = [
  { category: 'under', from: BMI_SCALE.min, to: 18.5 },
  { category: 'healthy', from: 18.5, to: 25 },
  { category: 'over', from: 25, to: 30 },
  { category: 'obese', from: 30, to: BMI_SCALE.max },
];

/** Where a BMI stands along the scale bar, as a percent clamped to the bar. */
export function bmiScalePercent(value: number): number {
  const share = (value - BMI_SCALE.min) / (BMI_SCALE.max - BMI_SCALE.min);
  return Math.round(Math.min(1, Math.max(0, share)) * 1000) / 10;
}

/**
 * A height re-expressed in the other unit, for a shopper who switches unit
 * after typing: what they typed carries over rather than vanishing. Empty in,
 * empty out; something unreadable is left for them to fix.
 */
export function convertHeight(
  to: BmiHeightUnit,
  height: string,
  inches: string,
): { height: string; inches: string } {
  if (to === 'ft') {
    const cm = readNumber(height);
    if (cm === null) return { height, inches: '' };
    let totalInches = Math.round(cm / CM_PER_INCH);
    const feet = Math.floor(totalInches / 12);
    totalInches -= feet * 12;
    return { height: String(feet), inches: String(totalInches) };
  }
  const feet = readNumber(height);
  if (feet === null) return { height, inches: '' };
  const total = feet * 12 + (readNumber(inches) ?? 0);
  return { height: String(Math.round(total * CM_PER_INCH)), inches: '' };
}

/** A weight re-expressed in the other unit, to one decimal place. */
export function convertWeight(to: BmiWeightUnit, weight: string): string {
  const value = readNumber(weight);
  if (value === null) return weight;
  const next = to === 'lb' ? value / KG_PER_POUND : value * KG_PER_POUND;
  return String(Math.round(next * 10) / 10);
}

/** The adult categories start here; younger shoppers are read on age charts. */
export const BMI_ADULT_AGE = 18;

/**
 * What an (optional) age says about reading the result: nothing typed is
 * fine, under 18 is a child, and anything that is not an age is refused.
 */
export function bmiAge(raw: string): 'none' | 'adult' | 'child' | 'invalid' {
  const age = readNumber(raw);
  if (age === null) return raw.trim() ? 'invalid' : 'none';
  if (age < 2 || age > 120) return 'invalid';
  return age < BMI_ADULT_AGE ? 'child' : 'adult';
}
